import type { ReviewSet } from "~/helpers/aiChat/migrationReview";
import type { UseFernieMigrationReviewReturn } from "~/hooks/useFernieMigrationReview";

import { Box } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import FernieLoadError from "~/components/fernie/FernieLoadError";
import FerniePreparationCard from "~/components/fernie/FerniePreparationCard";
import LINE_SX from "~/components/fernie/fernieStyles";
import { CaptionText } from "~/components/Typographies";
import migrationReview from "~/helpers/aiChat/migrationReview";
import useLocaleFormat from "~/hooks/useLocaleFormat";

interface FernieMigrationReviewProps {
	review: UseFernieMigrationReviewReturn;
}

const LIST_SX = { display: "flex", flexDirection: "column", gap: 0.75, listStyle: "none", m: 0, p: 0 } as const;

const FernieMigrationReview = ({ review }: FernieMigrationReviewProps) => {
	const { t } = useTranslation("common");
	const { formatDateTime } = useLocaleFormat();
	const [inspectedSourceId, setInspectedSourceId] = useState<string | null>(null);
	const { isError, journeyId, preparationCards, refetch, reviewSets, unfinishedElsewhere, waitingCount } = review;

	if (!journeyId || (!isError && preparationCards.length === 0 && reviewSets.length === 0)) return null;

	const renderSet = (set: ReviewSet) => {
		const copy = migrationReview.reviewSetCopy(set);
		const fileName = set.fileName
			? migrationReview.inertCellText(set.fileName)
			: t("fernieChat.review.preparation.unnamedFile");
		return (
			<Box
				component="li"
				key={`${set.kind}:${set.subjectId}`}
				sx={{ border: "1px solid", borderColor: "divider", borderRadius: "6px", px: 1, py: 0.75 }}
			>
				<CaptionText bold sx={LINE_SX}>
					{t(copy.titleKey)}
				</CaptionText>
				{set.revision !== null && (
					<CaptionText sx={LINE_SX}>
						{t("fernieChat.review.set.revision", { fileName, revision: set.revision })}
					</CaptionText>
				)}
				{set.operationCount > 0 && (
					<>
						<CaptionText sx={LINE_SX}>{t("fernieChat.review.set.records", { count: set.operationCount })}</CaptionText>
						<CaptionText sx={LINE_SX}>{migrationReview.familyListText(set.families, t)}</CaptionText>
					</>
				)}
				{set.presentedAt && (
					<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
						{t("fernieChat.review.set.presentedAt", { time: formatDateTime(set.presentedAt) })}
					</CaptionText>
				)}
				<CaptionText sx={{ ...LINE_SX, color: set.isStale ? "warning.main" : "text.secondary" }}>
					{t(copy.hintKey)}
				</CaptionText>
			</Box>
		);
	};

	return (
		<Box
			aria-label={t("fernieChat.review.regionLabel")}
			component="section"
			sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
		>
			{isError && <FernieLoadError onRetry={refetch} />}
			{waitingCount > 1 && (
				<CaptionText sx={LINE_SX}>{t("fernieChat.review.severalWaiting", { count: waitingCount })}</CaptionText>
			)}
			{reviewSets.length > 0 && (
				<Box component="ul" sx={LIST_SX}>
					{reviewSets.map(renderSet)}
				</Box>
			)}
			{preparationCards.length > 0 && (
				<Box component="ul" sx={LIST_SX}>
					{preparationCards.map((card) => (
						<FerniePreparationCard
							card={card}
							isInspecting={inspectedSourceId === card.sourceId}
							journeyId={journeyId}
							key={card.jobId}
							onToggleInspect={() =>
								setInspectedSourceId((current) => (current === card.sourceId ? null : card.sourceId))
							}
						/>
					))}
				</Box>
			)}
			{unfinishedElsewhere.length > 0 && (
				<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
					{t("fernieChat.review.unfinishedElsewhere", { list: migrationReview.familyListText(unfinishedElsewhere, t) })}
				</CaptionText>
			)}
		</Box>
	);
};

export default FernieMigrationReview;
